/**
 * Générateur — Utiliser un contre-exemple pour démontrer qu'une assertion est
 * fausse.
 * Attendu ID : 939 | Niveau : 5ème | Domaine : Calcul littéral (cluster 6, O41)
 *
 * Source : doc prof « TANOED EXERCISEUR nombre et calcul », thème Calcul
 * littéral, cluster 6 (Créé n°24, Créé n°25 + Ex.6q fermé par le doc).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « carres » — Créé n°24 Somme de deux carrés parfaits (l'exemple du
 *   cadrage) : texte_trou_saisie (4 sommes de deux carrés parfaits dont
 *   EXACTEMENT UNE est un carré parfait — couple pythagoricien + 3 sommes
 *   non-carrées VÉRIFIÉES par racine entière) + qcm_unique (« laquelle est
 *   un carré parfait ? ») + qcm_unique contre-exemple. ADAPTATION du ⚠️ du
 *   doc (« plusieurs contre-exemples acceptés ») : un SEUL contre-exemple
 *   valide parmi les choix, les autres étant le cas pythagoricien (« donc
 *   c'est vrai ») et des énoncés hors sujet.
 *
 *   Carte « relier » — Créé n°25 Trouver le contre-exemple : relier
 *   affirmation fausse ↔ contre-exemple, 4 paires parmi 6 familles.
 *   INVARIANT CENTRAL : chaque contre-exemple ne réfute QUE son affirmation —
 *   garanti par les pools (multiples de 5 pris dans {20, 40, 80} = multiples
 *   de 4, pour ne pas réfuter aussi « tout pair est multiple de 4 » ; pairs
 *   non multiples de 4 pris dans {6, 14, 18, 22}, jamais 10 qui réfuterait
 *   aussi les multiples de 5).
 *
 *   Carte « parfois » — Ex.6q (la production ouverte du doc, fermée par le
 *   doc lui-même) : qcm_unique « quel couple donne des résultats égaux pour
 *   x = 1 mais différents pour x = 2 ? » — correct (ax, (a−1)x + 1) par
 *   construction ; distracteurs : couple toujours égal (k(x+1), kx+k),
 *   jamais égal (kx, kx+c), égal ailleurs (x, 2x−w avec w ≥ 3). +
 *   texte_trou_saisie de vérification des 4 valeurs.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, makeRelier, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 939;

const L_CARRES = 'Somme de deux carrés parfaits : vrai ou faux ?';
const L_RELIER = 'Relier chaque affirmation à son contre-exemple';
const L_PARFOIS = 'Égales parfois, mais pas toujours';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement)

// racine carrée entière exacte (recherche bornée, indépendante de Math.sqrt)
const estCarreParfait = (v) => {
    for (let r = 0; r * r <= v; r++) if (r * r === v) return true;
    return false;
};

// ═════════ Carte 1 — Somme de deux carrés parfaits (Créé n°24) ═════════

// Contrainte 5e : on ne somme QUE des carrés parfaits connus (0² à 12²) et la
// somme ne dépasse jamais 12² = 144, pour que l'élève puisse décider si le
// résultat est un carré parfait.
const MAX_SOMME = 144;

// Les DEUX seules sommes de deux carrés parfaits ≤ 144 qui soient elles-mêmes
// un carré parfait : 3²+4²=25 et 6²+8²=100 (vérifié : aucune autre sous 144).
const PYTHA = [[3, 4], [6, 8]];

/** { paires: [[c1², c2², somme]], indexCarre } — exactement UNE somme carrée,
 *  tous les termes sont des carrés parfaits et toutes les sommes ≤ 144. */
const tirerSommesCarres = () => {
    const [pa, pb] = randomChoice(PYTHA);
    const paires = [[pa * pa, pb * pb, pa * pa + pb * pb]];
    const sommesVues = new Set([pa * pa + pb * pb]);
    // Lignes fausses : deux carrés parfaits (a², b², a<b), somme ≤ 144, NON carrée,
    // sommes distinctes. b jusqu'à 11 (11²=121) ; b=12 (144) forcerait l'autre à 0.
    for (let essai = 0; essai < 120 && paires.length < 4; essai++) {
        const a = randInt(1, 10);
        const b = randInt(a + 1, 11);
        const s = a * a + b * b;
        if (s > MAX_SOMME || estCarreParfait(s) || sommesVues.has(s)) continue;
        sommesVues.add(s);
        paires.push([a * a, b * b, s]);
    }
    // repli déterministe (sommes ≤ 144, non carrées, distinctes)
    const REPLI = [[4, 9, 13], [1, 16, 17], [16, 25, 41], [9, 100, 109], [1, 4, 5]];
    for (let i = 0; i < REPLI.length && paires.length < 4; i++) {
        if (!sommesVues.has(REPLI[i][2])) { sommesVues.add(REPLI[i][2]); paires.push(REPLI[i]); }
    }
    const melangees = shuffle(paires);
    return { paires: melangees, indexCarre: melangees.findIndex(p => estCarreParfait(p[2])) };
};

const TEXTE_CARRES = '<p><b>Chacun des nombres de cet exercice est un carré parfait '
    + '(par exemple 9 = 3² et 16 = 4²).</b> ';

// Tableau Vrai/Faux : tester chaque somme (carré parfait ou non) PUIS conclure
// que l'affirmation universelle est fausse — la logique du contre-exemple
// incarnée (attendu 939). Remplace l'ancien texte_trou « calcule les sommes »,
// hors sujet (préchauffage arithmétique). Le tirage est INCHANGÉ.
const genCarresVraiFaux = () => {
    const t = tirerSommesCarres();
    // 0 = Vrai (col 0), 1 = Faux (col 1) — exactement UNE somme est un carré parfait
    const lignes = t.paires.map(([a, b, s]) => `${a} + ${b} = ${s} est un carré parfait`);
    const bonnes = t.paires.map((_, i) => (i === t.indexCarre ? 0 : 1));
    // Ligne de conclusion : l'affirmation « toujours » est FAUSSE (un contre-exemple suffit)
    lignes.push('L’affirmation « la somme de deux carrés parfaits est toujours '
        + 'un carré parfait » est vraie');
    bonnes.push(1);   // Faux
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Zoé affirme : « La somme de deux carrés parfaits est toujours un '
        + 'carré parfait. »</b> Pour chaque ligne, indique si elle est <b>Vraie</b> '
        + 'ou <b>Fausse</b>.</p>',
        opts(L_CARRES));
    payload._meta.params = { paires: t.paires, indexCarre: t.indexCarre };
    return payload;
};

const genCarresQcm = () => {
    const t = tirerSommesCarres();
    const listing = t.paires.map(([a, b, s]) => `${a} + ${b} = ${s}`).join(' ; ');
    const correct = String(t.paires[t.indexCarre][2]);
    const autres = t.paires.filter((_, i) => i !== t.indexCarre).map(p => String(p[2]));
    const payload = makeQcm([correct, ...autres], correct,
        TEXTE_CARRES + `On a calculé : ${listing}. Parmi ces quatre sommes, laquelle est un carré parfait ?</p>`,
        opts(L_CARRES));
    payload._meta.params = { paires: t.paires, indexCarre: t.indexCarre };
    return payload;
};

const genContreExempleQcm = () => {
    const t = tirerSommesCarres();
    const [pa2, pb2, ps] = t.paires[t.indexCarre];
    const nonCarree = randomChoice(t.paires.filter((_, i) => i !== t.indexCarre));
    const [na, nb, ns] = nonCarree;
    const texte = '<p><b>L\'affirmation « la somme de deux carrés parfaits est toujours '
        + 'un carré parfait » est FAUSSE.</b> Quel calcul le prouve ?</p>';
    const correct = `${na} + ${nb} = ${ns}, et ${ns} n'est pas un carré parfait.`;
    const candidats = [
        `${pa2} + ${pb2} = ${ps}, qui est un carré parfait : l'affirmation est donc vraie.`,
        `${ns} est plus grand que ${na} et que ${nb}.`,
        `${na} et ${nb} sont tous les deux des carrés parfaits.`,
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte, opts(L_CARRES));
    payload._meta.params = { nonCarree, pytha: [pa2, pb2, ps] };
    return payload;
};

// ═════════ Carte 2 — Relier affirmation ↔ contre-exemple (Créé n°25) ═════════

// pools construits pour qu'un contre-exemple ne réfute QUE sa propre
// affirmation (cf. en-tête du plan de cartes)
const FAMILLES_CE = [
    {
        id: 'sommeImpairs',
        aff: 'La somme de deux nombres impairs est impaire',
        cible: () => {
            const a = randomChoice([3, 5, 7, 9]);
            let b = randomChoice([3, 5, 7, 9]);
            if (b === a) b = a === 9 ? 3 : a + 2;
            return `${a} + ${b} = ${a + b}`;
        },
    },
    {
        id: 'multiple5',
        aff: 'Tout multiple de 5 se termine par le chiffre 5',
        cible: () => String(randomChoice([20, 40, 80])),   // multiples de 4 aussi : ne réfutent pas « pair ⇒ multiple de 4 »
    },
    {
        id: 'carrePlusGrand',
        aff: 'Le carré d\'un nombre est toujours plus grand que ce nombre',
        cible: () => '1² = 1',
    },
    {
        id: 'pairMultiple4',
        aff: 'Tout nombre pair est un multiple de 4',
        cible: () => String(randomChoice([6, 14, 18, 22])),   // jamais 10 (réfuterait aussi les multiples de 5)
    },
    {
        id: 'sommeCarres',
        aff: 'La somme de deux carrés parfaits est toujours un carré parfait',
        cible: () => randomChoice(['4 + 9 = 13', '1 + 4 = 5', '16 + 25 = 41']),
    },
    {
        id: 'doublePlusGrand',
        aff: 'Le double d\'un nombre est toujours plus grand que ce nombre',
        cible: () => { const k = randInt(2, 5); return `2 × (${MOINS}${k}) = ${MOINS}${2 * k}`; },
    },
];

const genRelierCe = () => {
    const familles = shuffle(FAMILLES_CE).slice(0, 4);
    const pairs = familles.map(f => ({ source: f.aff, target: f.cible() }));
    const payload = makeRelier(pairs,
        '<p><b>Chacune de ces affirmations est FAUSSE.</b> Relie chaque affirmation '
        + 'au contre-exemple qui le prouve.</p>',
        opts(L_RELIER));
    payload._meta.params = { familles: familles.map(f => f.id) };
    return payload;
};

// ═════════ Carte 3 — Égales parfois, mais pas toujours (Ex.6q) ═════════

// rendu d'un coefficient (jamais « 1x »)
const cx = (c, L) => (c === 1 ? L : `${c}${L}`);

/** Le couple correct (ax, (a−1)x + 1) : égal en x = 1 SEULEMENT. */
const tirerParfois = () => {
    const a = randInt(2, 5);
    const k = randInt(2, 5);
    const c = randInt(1, 5);
    const w = randInt(3, 6);
    return {
        a, k, c, w,
        correct: `${a}x et ${cx(a - 1, 'x')} + 1`,
        jamais: `${k}x et ${k}x + ${c}`,
        toujours: `${k}(x + 1) et ${k}x + ${k}`,
        ailleurs: `x et 2x ${MOINS} ${w}`,
    };
};

const genParfoisQcm = () => {
    const t = tirerParfois();
    const texte = '<p><b>Parmi ces couples d\'expressions, lequel donne des résultats '
        + 'ÉGAUX pour x = 1 mais DIFFÉRENTS pour x = 2 ?</b> Calcule pour vérifier.</p>';
    const candidats = [t.jamais, t.toujours, t.ailleurs];
    const payload = makeQcm([t.correct, ...candidats], t.correct, texte, opts(L_PARFOIS));
    payload._meta.params = { a: t.a, k: t.k, c: t.c, w: t.w };
    return payload;
};

const genParfoisTrous = () => {
    // E₁ = ax + c et E₂ = (a−1)x + (c+1) : égales en x = 1 SEULEMENT, quel que
    // soit c (a + c = a − 1 + c + 1) — c élargit l'espace des tirages
    const a = randInt(2, 5);
    const c = randInt(0, 5);
    const e1 = c === 0 ? `${a}x` : `${a}x + ${c}`;
    const e2 = `${cx(a - 1, 'x')} + ${c + 1}`;
    const v1 = (x) => a * x + c;
    const v2 = (x) => (a - 1) * x + c + 1;
    const texte = `<p><b>On compare ${e1} et ${e2}.</b> Calcule chaque expression `
        + 'pour x = 1, puis pour x = 2. Que remarques-tu ?</p>';
    const phrase = `Pour x = 1 : ${e1} = {{${v1(1)}}} et ${e2} = {{${v2(1)}}}. `
        + `Pour x = 2 : ${e1} = {{${v1(2)}}} et ${e2} = {{${v2(2)}}}.`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PARFOIS), mode: 'saisie' });
    payload._meta.params = { a, c };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    carres_vrai_faux: genCarresVraiFaux,
    carres_qcm_unique: genCarresQcm,
    carres_contre_qcm_unique: genContreExempleQcm,
    relier_relier: genRelierCe,
    parfois_qcm_unique: genParfoisQcm,
    parfois_texte_trou_saisie: genParfoisTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier', vrai_faux: 'Vrai/Faux',
    texte_trou_saisie: 'Saisie trous', contre_qcm_unique: 'QCM contre-exemple',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'carres', label: L_CARRES, formatsList: buildFormats('carres') },
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'parfois', label: L_PARFOIS, formatsList: buildFormats('parfois') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
